import { AlertCircle, CheckCircle2, KeyRound, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ROUTES } from '@/constants/routes'

type Step = 'request' | 'success'

export const RecoverPasswordPage = () => {
	const [registration, setRegistration] = useState('')
	const [isPending, setIsPending] = useState(false)
	const [step, setStep] = useState<Step>('request')
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState('')

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsError(false)
		setIsPending(true)

		try {
			// TODO: call recover-password API endpoint when available
			await new Promise((resolve) => setTimeout(resolve, 800))
			setStep('success')
		} catch (_err) {
			setIsError(true)
			setError('Erro ao solicitar recuperação. Tente novamente.')
		} finally {
			setIsPending(false)
		}
	}

	return (
		<div className="min-h-dvh bg-white sm:flex sm:items-center sm:justify-center sm:bg-kaefer-light-silver sm:p-6">
			<div className="flex min-h-dvh w-full flex-col sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:bg-white sm:shadow-xl">
				{/* Header — dark banner on mobile, plain on tablet/desktop */}
				<div className="bg-kaefer-dark-grey px-6 pb-8 pt-10 text-center sm:rounded-t-2xl sm:px-8 sm:pb-6 sm:pt-8">
					<div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
						{step === 'success' ? (
							<CheckCircle2 className="h-7 w-7 text-white" />
						) : (
							<KeyRound className="h-7 w-7 text-white" />
						)}
					</div>
					<h1 className="text-2xl font-semibold text-white">
						{step === 'success' ? 'Solicitação enviada' : 'Recuperar senha'}
					</h1>
					<p className="mt-1.5 text-sm text-white/75">
						{step === 'success' ? (
							<>
								Sua solicitação foi registrada.
								<br />
								Entre em contato com o seu gestor ou suporte técnico.
							</>
						) : (
							<>
								Informe sua matrícula e enviaremos
								<br />
								as instruções para redefinir sua senha.
							</>
						)}
					</p>
				</div>

				{/* Body */}
				<div className="flex-1 px-6 py-8 sm:px-8 sm:pb-8 sm:pt-6">
					{step === 'request' ? (
						<form className="space-y-5" onSubmit={onSubmit}>
							<div className="space-y-2">
								<Label
									className="font-medium text-kaefer-dark-grey text-sm"
									htmlFor="registration"
								>
									Matrícula
								</Label>
								<div className="flex h-11 items-center rounded-md border border-kaefer-silver px-3 focus-within:border-kaefer-dark-grey">
									<Input
										className="flex-1 border-0 p-0 shadow-none outline-none focus-visible:ring-0"
										disabled={isPending}
										id="registration"
										onChange={(e) => setRegistration(e.target.value)}
										placeholder="Digite sua matrícula"
										required
										type="text"
										value={registration}
									/>
								</div>
							</div>

							{isError && (
								<Alert variant="destructive">
									<AlertCircle className="h-4 w-4" />
									<AlertDescription>{error}</AlertDescription>
								</Alert>
							)}

							<Button
								className="h-11 w-full font-medium text-base"
								disabled={isPending}
								type="submit"
								variant="kaefer-grey"
							>
								{isPending ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Enviando...
									</>
								) : (
									'Enviar instruções'
								)}
							</Button>

							<p className="text-center text-sm text-kaefer-medium-grey">
								Lembrou a senha?{' '}
								<Link
									className="font-medium text-kaefer-dark-grey transition-colors hover:text-kaefer-medium-grey"
									to={ROUTES.PUBLIC.LOGIN}
								>
									Voltar ao login
								</Link>
							</p>
						</form>
					) : (
						<div className="space-y-5">
							<div className="rounded-xl border border-kaefer-light-silver bg-kaefer-light-silver/40 px-4 py-4 text-sm text-kaefer-medium-grey">
								Sua matrícula{' '}
								<span className="font-semibold text-kaefer-dark-grey">
									{registration}
								</span>{' '}
								foi registrada. Um administrador irá redefinir sua senha em
								breve.
							</div>

							<Link to={ROUTES.PUBLIC.LOGIN}>
								<Button
									className="h-11 w-full font-medium text-base"
									variant="kaefer-grey"
								>
									Voltar ao login
								</Button>
							</Link>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
